/**
 * One item on one screen: practice (surfaces.md §1.1) and test (§1.2).
 *
 * The register, carried over unchanged from ugio_klase: „Taip.", „Ne visai.",
 * „Noriu suprasti", „Teisingas atsakymas". The verdict rides the button row,
 * left of the buttons, so nothing jumps under her hand. A commit is a button
 * or Enter, never the click itself. No counter, no clock, no mark.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, NetworkError } from "../lib/http";
import { ulid } from "../lib/ulid";
import { Rich, Tex } from "../lib/math";
import { type ChildExplanation, type ChildItem, type ChildTruth, type ChildVerdict, childApi } from "./api";
import { AnswerSurface, SURFACE_REV, type SurfaceHandle, promptOf } from "./surfaces/AnswerSurface";
import { Recorder } from "./trace";
import { envFor } from "./env";
import { type Phase, afterVerdict, answerText, isClosed } from "./flow/practice";
import { Explain } from "./Explain";
import { Rung, type RungHandle, ladderLength } from "./Rung";
import { useChild, useKeys } from "./ctx";
import { Aids, useAid } from "./aids/Aids";

/** What an item remembers across its variants (the ladder). */
export interface LadderState {
  /** attempt 1's handle: the item. */
  first: string;
  opened: string | null;
  rung: number;
  explanation: ChildExplanation | null;
  /** The variant she last missed, for the collapsed rung above a retry. */
  missed: { item: ChildItem; picked: string } | null;
}

export function newLadder(first: string): LadderState {
  return { first, opened: null, rung: 0, explanation: null, missed: null };
}

function labelsOf(item: ChildItem): Record<string, string> {
  const out: Record<string, string> = {};
  for (const o of item.options ?? []) out[o.id] = o.label;
  return out;
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * The answer, posted alone and at once (events.md §8.4), after the queue is
 * flushed so her beats precede it in `seq`. A network failure retries with the
 * same id, which makes the retry idempotent end to end.
 */
async function postAnswer(
  build: (attempt: number) => Parameters<typeof childApi.answer>[0],
  attempt: number,
  onOffline: (off: boolean) => void,
): Promise<{ v: ChildVerdict; attempt: number }> {
  const a = attempt;
  for (let tries = 0; ; tries += 1) {
    try {
      const v = await childApi.answer(build(a));
      onOffline(false);
      return { v, attempt: a };
    } catch (e) {
      if (e instanceof NetworkError || (e instanceof ApiError && e.status >= 500)) {
        onOffline(true);
        await sleep(Math.min(8000, 1500 * (tries + 1)));
        continue;
      }
      throw e;
    }
  }
}

/** The truth as she reads it: a number in math, an option by its label (which may hold math). */
export function TruthView({ truth }: { truth: ChildTruth }) {
  if (truth.option !== null || truth.tex === truth.text) return <Rich text={truth.text} />;
  return <Tex tex={truth.tex} />;
}

/* ============================================================================ practice */

export function PracticeItem({
  item,
  attempt,
  ladder,
  onNext,
  onRetry,
}: {
  item: ChildItem;
  attempt: number;
  ladder: LadderState;
  onNext: () => void;
  onRetry: (l: LadderState) => void;
}) {
  const ctx = useChild();
  const recorder = useMemo(() => new Recorder(), [item.handle]);
  const surface = useRef<SurfaceHandle | null>(null);
  const [phase, setPhase] = useState<Phase>({ at: "KLAUSIAMA" });
  const [slow, setSlow] = useState(false);
  const [offline, setOffline] = useState(false);
  const [failure, setFailure] = useState("");
  const [lad, setLad] = useState<LadderState>(ladder);
  const [mine, setMine] = useState<string>("");
  const [showPrev, setShowPrev] = useState(false);
  const answerId = useRef<string>(ulid());
  const sending = useRef(false);
  const rungHandle = useRef<RungHandle | null>(null);
  const of = ladderLength(item.drawing);
  const openedAt = useRef<number>(0);
  const aid = useAid(item, recorder);

  // Where she is, for a request's context and for „Baigti".
  useEffect(() => {
    const inExplanation = phase.at === "BEATS" || phase.at === "RUNG";
    ctx.setWhere({
      item: item.handle,
      state: phase.at,
      rung: phase.at === "RUNG" ? phase.rung : lad.rung,
      where: inExplanation ? "explanation" : "item",
      open: !isClosed(phase),
      onLeave: () => {
        rungHandle.current?.leave();
        if (lad.opened && inExplanation) closeExplanation("session_end");
        aid.close("session_end");
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, lad, item.handle]);
  useEffect(() => () => ctx.setWhere(null), [ctx]);

  useEffect(() => {
    const onVis = () => recorder.visibility(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [recorder]);

  const closeExplanation = (by: "retry" | "next_item" | "session_end" | "close") => {
    if (!lad.opened) return;
    const open = Math.round(performance.now() - openedAt.current);
    ctx.emit("explanation.closed", {
      opened: lad.opened,
      by,
      open_ms: open,
      visible_ms: open,
      last_beat: Math.max(0, (lad.explanation?.beats.length ?? 1) - 1),
      max_rung: lad.rung,
    });
  };

  const commit = useCallback(async () => {
    if (sending.current || !surface.current || surface.current.empty()) return;
    sending.current = true;
    setFailure("");
    const input = surface.current.input();
    setMine(answerText(input, labelsOf(item)));
    setPhase({ at: "TIKRINAMA" });
    const timer = setTimeout(() => setSlow(true), 400);
    const trace = recorder.trace();
    const timing = recorder.timing();
    try {
      await ctx.queue.flush();
      const { v } = await postAnswer(
        (a) => ({
          id: answerId.current,
          item: item.handle,
          attempt: a,
          surface: { rev: SURFACE_REV },
          input,
          trace,
          timing,
          env: envFor(trace),
          ...(recorder.truncated() ? { trace_truncated: recorder.truncated() } : {}),
          client_at: Date.now(),
        }),
        attempt,
        setOffline,
      );
      const next = afterVerdict(v, { opened: lad.opened, rung: lad.rung, of });
      const missed = v.word === "Ne visai." ? { item, picked: answerText(input, labelsOf(item)) } : lad.missed;
      const l2 = { ...lad, missed, rung: next.at === "RUNG" ? next.rung : lad.rung };
      setLad(l2);
      if (next.at === "RUNG") openedAt.current = openedAt.current || performance.now();
      setPhase(next);
      if (isClosed(next)) {
        if (lad.opened) closeExplanation("next_item");
        void ctx.queue.flush(); // an item ending is a moment
      }
    } catch (e) {
      if (ctx.lost(e)) return;
      setPhase({ at: "KLAUSIAMA" });
      setFailure(e instanceof ApiError ? "Nepavyko. Spausk „Tikrinti“ dar kartą." : "Nėra ryšio.");
    } finally {
      clearTimeout(timer);
      setSlow(false);
      sending.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, attempt, lad, recorder, ctx, of]);

  const openDoor = async () => {
    if (phase.at !== "DURYS") return;
    const door = phase.door;
    if (!door) {
      onRetry(lad);
      return;
    }
    const opened = ulid();
    try {
      await ctx.queue.flush();
      const x = await childApi.openExplanation({ id: opened, item: item.handle, explanation: door.explanation, door: "after_wrong", answer: answerId.current });
      openedAt.current = performance.now();
      setLad((l) => ({ ...l, opened, explanation: x }));
      setPhase({ at: "BEATS", explanation: x, beat: 0, step: 0 });
    } catch (e) {
      if (!ctx.lost(e)) setFailure("Nepavyko atidaryti. Bandyk dar kartą.");
    }
  };

  const reopenBeats = async () => {
    if (!lad.explanation) return;
    const opened = ulid();
    try {
      const x = await childApi.openExplanation({
        id: opened,
        item: item.handle,
        explanation: lad.explanation.ref.split("/").pop() ?? "",
        door: "reopen",
      });
      rungHandle.current?.leave();
      openedAt.current = performance.now();
      setLad((l) => ({ ...l, opened, explanation: x }));
      setPhase({ at: "BEATS", explanation: x, beat: 0, step: 0 });
    } catch (e) {
      ctx.lost(e);
    }
  };

  const beatsDone = () => {
    const r = Math.max(1, lad.rung + 1);
    setLad((l) => ({ ...l, rung: r }));
    setPhase({ at: "RUNG", rung: r, trigger: lad.rung === 0 ? "door" : "reopen" });
  };

  const retry = () => {
    rungHandle.current?.leave();
    closeExplanation("retry");
    aid.close("item_end"); // the next variant is another item.shown
    void ctx.queue.flush();
    onRetry(lad);
  };

  const toliau = () => {
    aid.close("item_end");
    void ctx.queue.flush();
    onNext();
  };

  const act = () => {
    switch (phase.at) {
      case "DURYS":
        void openDoor();
        break;
      case "RUNG":
        retry();
        break;
      case "TAIP":
      case "ATSAKYMAS":
      case "NETIKRINTA":
        toliau();
        break;
    }
  };
  useKeys({ Enter: act }, phase.at !== "KLAUSIAMA" && phase.at !== "TIKRINAMA" && phase.at !== "BEATS");

  const live = phase.at === "KLAUSIAMA";
  const wrong = phase.at === "DURYS" || phase.at === "BEATS" || phase.at === "RUNG" || phase.at === "ATSAKYMAS";
  // While she is in the material the question stays and the answers go away
  // (ugio_klase's order: here is the question, here is the thing, now answer).
  const inMaterial = phase.at === "BEATS" || phase.at === "RUNG";
  const setHandle = useCallback((h: SurfaceHandle) => {
    surface.current = h;
  }, []);
  const onAction = useCallback(
    (a: "commit" | "skip") => {
      if (a === "commit") void commit();
    },
    [commit],
  );

  return (
    <div data-phase={phase.at} data-mode="practice" data-attempt={attempt} data-handle={item.handle} data-drawing={item.drawing?.id ?? ""}>
      {lad.missed && attempt > 1 && lad.opened && phase.at === "KLAUSIAMA" && (
        <div className="rung-line">
          <span>Ankstesnis bandymas</span>
          <button type="button" className="quiet-link" onClick={() => setShowPrev((s) => !s)}>
            {showPrev ? "Paslėpti" : "Parodyk dar kartą"}
          </button>
        </div>
      )}
      {showPrev && lad.missed && lad.opened && phase.at === "KLAUSIAMA" && (
        <Rung drawing={lad.missed.item.drawing} rung={Math.max(1, lad.rung)} of={of} opened={lad.opened} item={lad.missed.item.handle} trigger="reopen" picked={lad.missed.picked} />
      )}

      <div className="prompt" data-prompt>
        <Rich text={promptOf(item)} />
      </div>

      <div hidden={inMaterial}>
        <AnswerSurface key={item.handle} item={item} recorder={recorder} live={live} wrong={wrong} test={false} onAction={onAction} handle={setHandle} />
      </div>

      {phase.at === "BEATS" && lad.opened && <Explain explanation={phase.explanation} opened={lad.opened} onDone={beatsDone} />}

      {phase.at === "RUNG" && lad.opened && (
        <>
          <Rung
            drawing={item.drawing}
            rung={phase.rung}
            of={of}
            opened={lad.opened}
            item={item.handle}
            trigger={phase.trigger}
            picked={mine}
            bind={(h) => {
              rungHandle.current = h;
            }}
          />
        </>
      )}

      {phase.at === "ATSAKYMAS" && phase.truth && (
        <div className="truth" data-truth>
          <div className="truth-label">Teisingas atsakymas</div>
          <div className="truth-value">
            <TruthView truth={phase.truth} />
          </div>
          <div className="note">Tavo atsakymas: {mine}</div>
        </div>
      )}

      {offline && <p className="note">Nėra ryšio. Atsakymas palauktas.</p>}
      {failure && <p className="note">{failure}</p>}

      <div className="actions">
        {phase.at === "TAIP" && <p className="verdict verdict-right" data-verdict>Taip.</p>}
        {(phase.at === "DURYS" || phase.at === "ATSAKYMAS" || phase.at === "RUNG") && (
          <p className="verdict verdict-wrong" data-verdict>Ne visai.</p>
        )}
        {phase.at === "RUNG" && lad.explanation && (
          <button type="button" className="quiet-link" onClick={() => void reopenBeats()} data-act="reopen">
            Paaiškinimas
          </button>
        )}
        {(phase.at === "NETIKRINTA" || (phase.at === "TIKRINAMA" && slow)) && <p className="verdict verdict-wait">Tikrinama…</p>}

        {(phase.at === "KLAUSIAMA" || phase.at === "TIKRINAMA") && (
          <button type="button" className="btn" disabled={phase.at === "TIKRINAMA"} onClick={() => void commit()} data-act="check">
            Tikrinti <span className="key-hint">Enter</span>
          </button>
        )}
        {phase.at === "DURYS" && (
          <button type="button" className="btn" onClick={() => void openDoor()} data-act="door">
            {phase.door ? "Noriu suprasti" : "Bandyti dar kartą"} <span className="key-hint">Enter</span>
          </button>
        )}
        {phase.at === "RUNG" && (
          <button type="button" className="btn" onClick={retry} data-act="retry">
            Bandyti dar kartą <span className="key-hint">Enter</span>
          </button>
        )}
        {isClosed(phase) && (
          <button type="button" className="btn btn-solid" onClick={toliau} data-act="next">
            Toliau <span className="key-hint">Enter</span>
          </button>
        )}
      </div>
      <Aids aid={aid} />
    </div>
  );
}

/* ============================================================================ test */

export function TestItem({ item, onNext }: { item: ChildItem; onNext: () => void }) {
  const ctx = useChild();
  const recorder = useMemo(() => new Recorder(), [item.handle]);
  const surface = useRef<SurfaceHandle | null>(null);
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(false);
  const [failure, setFailure] = useState("");
  const id = useRef(ulid());
  const attempt = item.attempt;
  const aid = useAid(item, recorder); // a school test allows the table too
  const closeAid = aid.close;

  useEffect(() => {
    ctx.setWhere({ item: item.handle, state: "KLAUSIAMA", rung: 0, where: "item", open: true, onLeave: () => closeAid("session_end") });
    return () => ctx.setWhere(null);
  }, [ctx, item.handle, closeAid]);
  useEffect(() => {
    const onVis = () => recorder.visibility(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [recorder]);

  const send = useCallback(
    async (skip: boolean) => {
      if (busy || !surface.current) return;
      if (!skip && surface.current.empty()) return;
      setBusy(true);
      setFailure("");
      const input = surface.current.input();
      const trace = recorder.trace();
      try {
        await ctx.queue.flush();
        if (skip) {
          const t = recorder.timing();
          for (;;) {
            try {
              await childApi.skip({
                id: id.current,
                item: item.handle,
                attempt,
                input,
                timing: { skip_ms: t.submit_ms, hidden_ms: t.hidden_ms, first_key_ms: t.first_key_ms },
                trace,
                client_at: Date.now(),
              });
              break;
            } catch (e) {
              if (e instanceof NetworkError || (e instanceof ApiError && e.status >= 500)) {
                setOffline(true);
                await sleep(2000);
                continue;
              }
              throw e;
            }
          }
        } else {
          const timing = recorder.timing();
          await postAnswer(
            (a) => ({ id: id.current, item: item.handle, attempt: a, surface: { rev: SURFACE_REV }, input, trace, timing, env: envFor(trace), client_at: Date.now() }),
            attempt,
            setOffline,
          );
        }
        setOffline(false);
        closeAid("item_end");
        onNext(); // no verdict, no transition: the next question replaces this one
      } catch (e) {
        if (ctx.lost(e)) return;
        if (e instanceof ApiError && e.status === 409 && /already answered/.test(e.message)) {
          closeAid("item_end");
          onNext();
          return;
        }
        setFailure("Nepavyko. Bandyk dar kartą.");
        setBusy(false);
      }
    },
    [busy, recorder, item.handle, attempt, ctx, onNext, closeAid],
  );

  const onAction = useCallback((a: "commit" | "skip") => void send(a === "skip"), [send]);
  const setHandle = useCallback((h: SurfaceHandle) => {
    surface.current = h;
  }, []);

  return (
    <div data-phase="KLAUSIAMA" data-mode="test" data-handle={item.handle}>
      <div className="prompt" data-prompt>
        <Rich text={promptOf(item)} />
      </div>
      <AnswerSurface key={item.handle} item={item} recorder={recorder} live={!busy} wrong={false} test onAction={onAction} handle={setHandle} />
      {offline && <p className="note">Nėra ryšio. Atsakymas palauktas.</p>}
      {failure && <p className="note">{failure}</p>}
      <div className="actions">
        <button
          type="button"
          className="btn btn-quiet"
          disabled={busy}
          data-act="skip"
          onClick={() => {
            recorder.push(["Mouse0", "btn:skip", "", "skip"]);
            void send(true);
          }}
        >
          Praleisti <span className="key-hint">Ctrl+Enter</span>
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy}
          data-act="test-next"
          onClick={() => {
            recorder.push(["Mouse0", "btn:toliau", "", "button"]);
            void send(false);
          }}
        >
          Toliau <span className="key-hint">Enter</span>
        </button>
      </div>
      <Aids aid={aid} />
    </div>
  );
}
